import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PLANBASE_CONFIG } from '../config';
import { decodeJWT } from '../utils/auth';
import { Spinner } from '../components/ui/spinner';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState('');

  useEffect(() => {
    // Handle OAuth callback
    const handleCallback = async () => {
      try {
        const queryParams = new URLSearchParams(window.location.search);
        
        // Debug: Log all query parameters
        console.log('AuthCallback - Current URL:', window.location.href);
        console.log('AuthCallback - Query params:', Object.fromEntries(queryParams.entries()));
        
        // Check for Gately token (from SSO redirect)
        const gatelyToken = queryParams.get('gately_token');
        const gatelySession = queryParams.get('gately_session');
        const gatelyUser = queryParams.get('gately_user');
        const authSuccess = queryParams.get('gately_auth_success');
        const errorParam = queryParams.get('error');
        
        console.log('AuthCallback - gatelyToken:', gatelyToken ? 'present' : 'missing');
        console.log('AuthCallback - gatelySession:', gatelySession ? 'present' : 'missing');
        console.log('AuthCallback - gatelyUser:', gatelyUser ? 'present' : 'missing');
        console.log('AuthCallback - authSuccess:', authSuccess);
        console.log('AuthCallback - error:', errorParam);
        
        if (errorParam) {
          setError(errorParam);
          setTimeout(() => navigate('/login'), 2000);
          return;
        }
        
        // Handle new format with gately_session and gately_user
        if (gatelySession && gatelyUser && authSuccess === 'true') {
          try {
            const sessionData = JSON.parse(gatelySession);
            const userData = JSON.parse(gatelyUser);
            
            console.log('AuthCallback - Parsed session data:', sessionData);
            console.log('AuthCallback - Parsed user data:', userData);
            
            // Extract token from session
            const token = sessionData.access_token;
            if (!token) {
              throw new Error('No access token in session data');
            }
            
            // Store the token
            localStorage.setItem('planbase_token', token);
            
            // Construct user object from the provided data
            const user = {
              id: userData.id,
              email: userData.email,
              name: userData.name || userData.full_name || userData.email?.split('@')[0] || 'User',
              avatar_url: userData.avatar_url,
              title: userData.metadata?.title || 'Architect',
              metadata: {
                ...userData.metadata,
                full_name: userData.name || userData.full_name
              }
            };
            
            localStorage.setItem('planbase_user', JSON.stringify(user));
            
            // Always try to notify opener and close — works when window.opener is available.
            // When opener is null (cross-origin redirect stripped it), the localStorage
            // polling in AuthContext will detect the token and resolve instead.
            try {
              if (window.opener && !window.opener.closed) {
                window.opener.postMessage({ type: 'GATELY_OAUTH_SUCCESS' }, window.location.origin);
              }
            } catch (_) {
              // opener may be cross-origin restricted — polling fallback handles it
            }
            // Always attempt to close the popup
            window.close();
            
            // If window.close() didn't work (e.g. not opened by script), fall through
            // to direct navigation after a short delay
            setTimeout(() => {
              if (!window.closed) {
                window.dispatchEvent(new Event('auth-changed'));
                navigate('/');
              }
            }, 300);
            return;
          } catch (parseError: any) {
            console.error('AuthCallback - Failed to parse session/user data:', parseError);
            setError('Failed to parse authentication data');
            setTimeout(() => navigate('/login'), 2000);
            return;
          }
        }
        
        // Handle legacy format with gately_token (fallback)
        if (gatelyToken && authSuccess === 'true') {
          // Store the token temporarily
          localStorage.setItem('planbase_token', gatelyToken);
          
          // Decode JWT to get user ID
          try {
            const payload = decodeJWT(gatelyToken);
            
            const userId = payload?.userId || payload?.sub;
            const projectId = payload.project_id || PLANBASE_CONFIG.projectId;
            
            // Fetch full user data from API
            const userResponse = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/members/${userId}`, {
              headers: {
                'Authorization': `Bearer ${gatelyToken}`,
                'X-Project-ID': projectId,
                'Content-Type': 'application/json'
              }
            });
            
            if (!userResponse.ok) {
              throw new Error('Failed to fetch user data');
            }
            
            const userData = await userResponse.json();
            
            // Extract member data
            const member = userData.member || userData;
            
            // Parse metadata if it's a string
            let metadata = member.metadata;
            if (typeof metadata === 'string') {
              try {
                metadata = JSON.parse(metadata);
              } catch (e) {
                metadata = {};
              }
            }
            
            // Construct full name from available fields
            const fullName = metadata.full_name || 
                           (metadata.first_name && metadata.last_name 
                             ? `${metadata.first_name} ${metadata.last_name}` 
                             : null) ||
                           metadata.first_name ||
                           metadata.name ||
                           member.email?.split('@')[0] || 
                           'User';
            
            const user = {
              id: member.id,
              email: member.email,
              name: fullName,
              avatar_url: metadata.avatar_url,
              title: metadata.title || 'Architect',
              metadata: {
                ...metadata,
                full_name: fullName
              }
            };
            
            localStorage.setItem('planbase_user', JSON.stringify(user));
            
            // Always try to notify opener and close
            try {
              if (window.opener && !window.opener.closed) {
                window.opener.postMessage({ type: 'GATELY_OAUTH_SUCCESS' }, window.location.origin);
              }
            } catch (_) {
              // opener may be cross-origin restricted — polling fallback handles it
            }
            window.close();

            setTimeout(() => {
              if (!window.closed) {
                window.dispatchEvent(new Event('auth-changed'));
                navigate('/');
              }
            }, 300);
          } catch (decodeError: any) {
            setError(decodeError.message || 'Authentication failed');
            setTimeout(() => navigate('/login'), 2000);
          }
        } else {
          setError('No authentication token received');
          setTimeout(() => navigate('/login'), 2000);
        }
      } catch (err: any) {
        setError(err.message || 'Authentication failed');
        setTimeout(() => navigate('/login'), 2000);
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-white">
      <div className="text-center">
        {error ? (
          <>
            <div className="mb-4 text-red-500">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-lg font-medium text-gray-900 mb-2">Authentication Failed</p>
            <p className="text-sm text-gray-600">{error}</p>
            <p className="text-sm text-gray-500 mt-2">Redirecting to login...</p>
          </>
        ) : (
          <>
            <Spinner size="lg" className="mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-900">Completing authentication...</p>
            <p className="text-sm text-gray-600 mt-2">Please wait</p>
          </>
        )}
      </div>
    </div>
  );
}
